/**
 * Role helpers mirroring the backend's core/auth.py semantics.
 *
 * The JWT carries no role — the frontend only knows user.role from
 * /api/auth/me. Canonical roles: owner, admin, project_manager, member,
 * subcontractor. Legacy rows can still hold pm, superintendent, estimator,
 * viewer. Backend ROLE_ALIASES treat pm ≡ project_manager and owner/admin
 * as a superset of everything, so these helpers do the same.
 *
 * These gates hide controls the backend would 403 anyway; they are a UX
 * courtesy, not the enforcement layer.
 */

export const isElevated = (user) => ["owner", "admin"].includes(user?.role);

export const isProjectManager = (user) =>
  ["project_manager", "pm"].includes(user?.role);

/** Award decisions, SA proposal decisions, requirement verify/supersede. */
export const canDecide = (user) => isElevated(user) || isProjectManager(user);

/** Schedule/activity/requirement mutations (adds superintendent). */
export const canEditSchedule = (user) =>
  canDecide(user) || user?.role === "superintendent";

/** Procurement/vendor/bid CRUD (adds estimator). */
export const canEditProcurement = (user) =>
  canDecide(user) || ["estimator", "superintendent"].includes(user?.role);

/**
 * Award recommendation generation: the backend propose endpoint admits
 * admin/project_manager/pm/superintendent — NOT estimator.
 */
export const canProposeAward = (user) =>
  canDecide(user) || user?.role === "superintendent";

/** External party — never show competing-bidder or evaluation internals. */
export const isSubcontractor = (user) => user?.role === "subcontractor";

/**
 * Commercial internals: leveling detail, competing bidder amounts,
 * evaluation rationale. Internal staff only.
 */
export const canViewCommercialInternals = (user) =>
  Boolean(user?.role) && !isSubcontractor(user);
